const PHOTO_ALBUM_SWIPE_PATCH = Symbol.for('eInvitation.photoAlbumSwipeNavigation.v1')

const MIN_SWIPE_DISTANCE = 52
const MAX_SWIPE_DURATION_MS = 1200
const AXIS_DOMINANCE_RATIO = 1.15
const CLICK_SUPPRESSION_MS = 420

const attachedStages = new WeakSet()

function stopGestureEvent(event) {
  if (event.cancelable) event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation?.()
}

function isInteractiveTarget(target) {
  if (!(target instanceof Element)) return false

  // Page 4 is now a vertically scrollable gallery. Never capture its pointer or
  // touch gestures for book navigation; scrolling the photos takes priority.
  if (target.closest('.epa-public-gallery-root')) return true

  // Labels and the upload drop zone must retain their native mobile tap
  // behavior. Capturing touchstart here prevents iOS/Android from opening the
  // file picker even though the hidden file input is correctly configured.
  const interactive = target.closest(
    'input, textarea, select, option, label, .epa-upload-drop, a, video, iframe, [contenteditable="true"], button'
  )

  if (interactive?.classList?.contains('epa-public-photo-button')) return false
  return Boolean(interactive)
}

function getNavigationButtons() {
  const nav = document.querySelector('.epa-bottom-nav')
  if (!nav) return null

  const buttons = nav.querySelectorAll('button')
  if (buttons.length < 2) return null

  return {
    previous: buttons[0],
    next: buttons[1],
  }
}

function navigateFromSwipe(direction) {
  const navigation = getNavigationButtons()
  if (!navigation) return false

  const button = direction === 'next' ? navigation.next : navigation.previous
  if (!button || button.disabled) return false

  button.click()
  return true
}

function isValidSwipe(start, end) {
  if (!start || !end) return null
  const deltaX = end.x - start.x
  const deltaY = end.y - start.y
  const duration = end.time - start.time
  const horizontalDistance = Math.abs(deltaX)
  const verticalDistance = Math.abs(deltaY)

  if (duration > MAX_SWIPE_DURATION_MS) return null
  if (horizontalDistance < MIN_SWIPE_DISTANCE) return null
  if (horizontalDistance < verticalDistance * AXIS_DOMINANCE_RATIO) return null
  return deltaX < 0 ? 'next' : 'previous'
}

function attachSwipeNavigation(stage) {
  if (!(stage instanceof HTMLElement) || attachedStages.has(stage)) return
  attachedStages.add(stage)

  let touchStart = null
  let mouseStart = null
  let trackingMouse = false
  let suppressClickUntil = 0

  const suppressFollowingClick = () => {
    suppressClickUntil = Date.now() + CLICK_SUPPRESSION_MS
  }

  const onClickCapture = (event) => {
    if (Date.now() >= suppressClickUntil) return
    stopGestureEvent(event)
  }

  const onTouchStart = (event) => {
    if (event.touches.length !== 1 || isInteractiveTarget(event.target)) {
      touchStart = null
      return
    }

    const touch = event.touches[0]
    touchStart = {
      x: touch.clientX,
      y: touch.clientY,
      time: performance.now(),
    }
    stopGestureEvent(event)
  }

  const onTouchMove = (event) => {
    if (!touchStart) return
    stopGestureEvent(event)
  }

  const onTouchEnd = (event) => {
    if (!touchStart) return
    const touch = event.changedTouches?.[0]
    const start = touchStart
    touchStart = null
    stopGestureEvent(event)

    if (!touch) return
    const direction = isValidSwipe(start, {
      x: touch.clientX,
      y: touch.clientY,
      time: performance.now(),
    })

    if (!direction) return
    suppressFollowingClick()
    navigateFromSwipe(direction)
  }

  const onTouchCancel = (event) => {
    if (!touchStart) return
    touchStart = null
    stopGestureEvent(event)
  }

  const onMouseMove = (event) => {
    if (!trackingMouse || !mouseStart) return
    stopGestureEvent(event)
  }

  const finishMouseGesture = (event) => {
    if (!trackingMouse || !mouseStart) return
    const start = mouseStart
    trackingMouse = false
    mouseStart = null
    stopGestureEvent(event)

    const direction = isValidSwipe(start, {
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
    })

    if (!direction) return
    suppressFollowingClick()
    navigateFromSwipe(direction)
  }

  const cancelMouseGesture = () => {
    trackingMouse = false
    mouseStart = null
  }

  const onMouseDown = (event) => {
    if (event.button !== 0 || isInteractiveTarget(event.target)) {
      cancelMouseGesture()
      return
    }

    trackingMouse = true
    mouseStart = {
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
    }
    stopGestureEvent(event)
  }

  stage.addEventListener('click', onClickCapture, true)
  stage.addEventListener('touchstart', onTouchStart, { capture: true, passive: false })
  stage.addEventListener('touchmove', onTouchMove, { capture: true, passive: false })
  stage.addEventListener('touchend', onTouchEnd, { capture: true, passive: false })
  stage.addEventListener('touchcancel', onTouchCancel, { capture: true, passive: false })
  stage.addEventListener('mousedown', onMouseDown, true)
  window.addEventListener('mousemove', onMouseMove, true)
  window.addEventListener('mouseup', finishMouseGesture, true)
  window.addEventListener('blur', cancelMouseGesture)
}

function scanForStage(root = document) {
  if (root instanceof Element && root.matches('.epa-stage')) {
    attachSwipeNavigation(root)
  }
  root.querySelectorAll?.('.epa-stage').forEach(attachSwipeNavigation)
}

export function installPhotoAlbumSwipeNavigation() {
  if (typeof document === 'undefined' || globalThis[PHOTO_ALBUM_SWIPE_PATCH]) return

  const start = () => {
    scanForStage(document)

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          scanForStage(node)
        }
      }
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[PHOTO_ALBUM_SWIPE_PATCH] = true
}
